// Menu groups (channels) + fulfilment modes: one canonical item with sparse
// deltas. This app IS the 'online' channel; within it, the fulfilment mode
// (?mode=delivery|pickup|dinein) further scopes pricing and menu visibility.
// Deltas are resolved ONCE, where the menu data enters the app — every
// consumer (catalogue, cart, checks, rewards) then sees plain productPrice /
// isDisabled / enabled fields and needs no changes. PPB applies the same
// resolution server-side from order_type; the client numbers are
// display-only, as ever.
//
// Price precedence (per item):
//   1. item's price for the CURRENT MODE   groupOverrides.online[mode].price
//   2. item's online channel price          groupOverrides.online.price
//   3. base productPrice × (1 + uplift%)    mode uplift ?? legacy uplift
//   4. base productPrice
// Explicit item prices (1 and 2) are exact — uplift never stacks on them.
//
// Menu visibility:
//   groupVisibility.online === false            hidden from online entirely
//   groupVisibility.onlineModes[mode] === false hidden from that mode only
//     (e.g. an alcohol menu excluded from delivery — PPB enforces too)

import type { GroupOverride } from "../types/menu";

export type FulfilmentMode = "delivery" | "pickup" | "dinein";

// Channel-wide uplift rides in menuSettings as the __channelConfig__
// sentinel entry (deleted+disabled so nothing renders it — it's the only
// doc shape that survives the pool's overwriteMenu replace).
const CHANNEL_CONFIG_KEY = "__channelConfig__";

/** Uplift % for the online channel, scoped to a mode when configured:
 *  groupPricing.online[mode].upliftPercent ?? groupPricing.online.upliftPercent. */
export function onlineUpliftPercent(menuSettings: any[], mode?: FulfilmentMode | null): number {
  const entry = (menuSettings || []).find((m: any) => m?.menuName === CHANNEL_CONFIG_KEY);
  const online = entry?.groupPricing?.online;
  const modeU = mode ? Number(online?.[mode]?.upliftPercent) : NaN;
  if (modeU > 0) return modeU;
  if (mode && online?.[mode] && Number(online[mode].upliftPercent) === 0) return 0;
  const u = Number(online?.upliftPercent);
  return u > 0 ? u : 0;
}

/** Bake online-channel + fulfilment-mode deltas into the product list.
 *  - enabled: false → isDisabled: true (items must stay IN the list so cart
 *    availability checks find them instead of crashing on undefined). */
export function resolveOnlineMenu(menu: any[], menuSettings?: any[], mode?: FulfilmentMode | null): any[] {
  const uplift = onlineUpliftPercent(menuSettings || [], mode);
  return (menu || []).map((p: any) => {
    const o: GroupOverride | undefined = p?.groupOverrides?.online;
    if (!o && !uplift) return p;
    const out = { ...p };
    const modePrice = mode ? o?.[mode]?.price : undefined;
    if (typeof modePrice === "number") out.productPrice = modePrice;
    else if (typeof o?.price === "number") out.productPrice = o.price;
    else if (uplift && typeof p.productPrice === "number") {
      out.productPrice = Math.round(p.productPrice * (1 + uplift / 100) * 100) / 100;
    }
    if (o?.enabled === false) out.isDisabled = true;
    return out;
  });
}

/** Menus hidden from the online channel — or from the current fulfilment
 *  mode — become enabled:false (not removed: cart validation indexes
 *  menuSettings by name). */
export function resolveOnlineMenuSettings(menuSettings: any[], mode?: FulfilmentMode | null): any[] {
  return (menuSettings || [])
    // The __channelConfig__ sentinel (channel pricing + save rev) is NOT a
    // menu: it has no categories, and code that maps every entry's
    // categories (e.g. the product modal's category-name lookup) crashed on
    // it. Strip it here — the one place every online consumer passes.
    .filter((m: any) => m?.menuName !== "__channelConfig__")
    .map((m: any) => {
      if (m?.groupVisibility?.online === false) return { ...m, enabled: false };
      if (mode && m?.groupVisibility?.onlineModes?.[mode] === false) return { ...m, enabled: false };
      return m;
    });
}
